/**
 * Weekly Growth Card — the Sunday round-up: chapters improved, streak, best
 * score. Students can send it to their parent or share it on WhatsApp.
 * `always` shows it any day (Growth page / parent view); otherwise it only
 * appears on Sunday and Monday.
 */
import { useEffect, useState } from 'react';
import { CalendarCheck2, Copy, Flame, Loader2, Send, Trophy, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '@/lib/api';

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-2xl bg-white/10 px-3 py-2.5" data-testid={testId}>
      <p className="flex items-center gap-1.5 text-[11px] font-heading text-white/70"><Icon className="w-3.5 h-3.5" /> {label}</p>
      <p className="font-display text-xl font-bold mt-0.5">{value}</p>
    </div>
  );
}

export function WeeklyGrowthCard({ childId = '', always = false, canShare = true }) {
  const [card, setCard] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const q = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    apiGet(`/growth/weekly${q}`).then(setCard).catch(() => setCard(null));
  }, [childId]);

  if (!card || (!always && !card.show_now)) return null;

  const sendToParent = async () => {
    setSending(true);
    try {
      const r = await apiPost('/growth/weekly/share', {});
      toast.success(r.sent_to > 1 ? `Sent to your ${r.sent_to} parents` : 'Sent to your parent');
    } catch (e) { toast.error(e?.message || 'Could not send it'); }
    setSending(false);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(card.share_text); toast.success('Copied — paste it anywhere'); }
    catch { toast.error('Could not copy'); }
  };
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(card.share_text)}`;

  return (
    <section className="rounded-3xl bg-gradient-to-br from-indigo-700 via-violet-700 to-fuchsia-700 text-white p-4 sm:p-5 shadow-lg" data-testid="weekly-growth-card">
      <div className="flex items-center justify-between gap-2">
        <p className="font-heading text-sm font-bold flex items-center gap-1.5"><CalendarCheck2 className="w-4 h-4" /> {childId ? `${card.first_name || 'Their'}'s week` : (card.show_now ? 'Your week' : 'Last week')} · {fmt(card.week_start)} – {fmt(card.week_end)}</p>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <Stat icon={TrendingUp} label="Chapters up" value={card.chapters_improved.length} testId="weekly-chapters-improved" />
        <Stat icon={Flame} label="Streak" value={`${card.streak.current}d`} testId="weekly-streak" />
        <Stat icon={Trophy} label="Best score" value={card.best_score ? `${card.best_score.pct}%` : '—'} testId="weekly-best-score" />
      </div>
      {card.chapters_improved.length > 0 ? (
        <ul className="mt-3 space-y-1" data-testid="weekly-improved-list">
          {card.chapters_improved.slice(0, 4).map(c => (
            <li key={`${c.subject}-${c.chapter}`} className="text-[12.5px] font-body text-white/90 flex justify-between gap-2">
              <span className="truncate">{c.chapter} <span className="text-white/60">· {c.subject}</span></span>
              <span className="font-heading flex-shrink-0">{c.from}% → {c.to}%</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-[12.5px] font-body text-white/80">
          {childId
            ? (card.quizzes ? 'No chapter beat its last score that week.' : 'No quiz that week — a gentle nudge to take one helps the next card show real progress.')
            : (card.quizzes ? 'No chapter beat its last score that week — retry one you found tricky.' : 'No quiz that week. Take one this week — on Sunday I\'ll show how much you improved.')}
        </p>
      )}
      {card.best_score && <p className="mt-1.5 text-[11.5px] font-body text-white/70">Best: {card.best_score.chapter} ({card.best_score.subject}) · {card.sessions} study sessions</p>}
      {canShare && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={sendToParent} disabled={sending} data-testid="weekly-send-parent"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-indigo-700 text-xs font-heading hover:bg-indigo-50 disabled:opacity-60">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send to my parent
          </button>
          <a href={whatsapp} target="_blank" rel="noreferrer" data-testid="weekly-share-whatsapp"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-heading">
            Share on WhatsApp
          </a>
          <button onClick={copy} data-testid="weekly-copy"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-heading">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
        </div>
      )}
    </section>
  );
}

export default WeeklyGrowthCard;
